import { useState, useEffect, useCallback } from 'react';
import { hrms } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import Modal from '../../components/common/Modal';
import Table from '../../components/common/Table';
import { theme } from '../../theme';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const STATUS_FLOW = {
  draft:       { label: 'Pending HR Approval', color: theme.warning },
  pending_cfo: { label: 'Pending CFO Approval', color: theme.primary },
  approved:    { label: 'Approved', color: theme.success },
  paid:        { label: 'Paid', color: theme.success },
};

function Payroll() {
  const { user } = useAuth();
  const role = user?.role;

  const now = new Date();
  const [selMonth, setSelMonth] = useState(now.getMonth() + 1);
  const [selYear, setSelYear] = useState(now.getFullYear());

  const [period, setPeriod] = useState(null);
  const [records, setRecords] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [runModal, setRunModal] = useState(false);
  const [running, setSaving] = useState(false);

  // Load all periods once (for existence check)
  const loadPeriods = useCallback(async () => {
    try {
      const r = await hrms.getPayrollPeriods();
      setPeriods(r.data || []);
    } catch {}
  }, []);

  useEffect(() => { loadPeriods(); }, [loadPeriods]);

  // Load records + period whenever month/year changes
  const loadRecords = useCallback(async () => {
    const found = periods.find(p => p.month === selMonth && p.year === selYear);
    setPeriod(found || null);
    if (!found) { setRecords([]); return; }
    setLoading(true);
    try {
      const r = await hrms.getPayrollRecords({ period_id: found.id });
      const d = r.data;
      setRecords(Array.isArray(d) ? d : d.items || []);
    } catch { setRecords([]); } finally { setLoading(false); }
  }, [selMonth, selYear, periods]);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  const handleRunPayroll = async (force = false) => {
    setSaving(true);
    try {
      const res = await hrms.runPayroll({ month: selMonth, year: selYear, force });
      setRunModal(false);
      await loadPeriods();
      const d = res.data;
      if (d.skipped > 0) {
        const reasons = d.skip_reasons?.length
          ? '\n\nSkipped:\n' + d.skip_reasons.join('\n')
          : '';
        alert(`${d.created} record(s) created, ${d.skipped} skipped.${reasons}`);
      }
    } catch (e) { alert('Failed: ' + (e.response?.data?.detail || e.message)); } finally { setSaving(false); }
  };

  const handleHrApprove = async () => {
    if (!window.confirm(`Approve payroll for ${MONTHS[selMonth - 1]} ${selYear} and forward to CFO?`)) return;
    try {
      await hrms.hrApprovePayroll(period.id);
      await loadPeriods();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const handleCfoApprove = async () => {
    if (!window.confirm(`Final approval for ${MONTHS[selMonth - 1]} ${selYear}? Employee payslips will become visible.`)) return;
    try {
      await hrms.cfoApprovePayroll(period.id);
      await loadPeriods();
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };

  const canRun = ['hr_admin', 'hr_head', 'cfo', 'super_admin'].includes(role);
  const canHrApprove = ['hr_admin', 'hr_head', 'super_admin'].includes(role);
  const canCfoApprove = ['cfo', 'super_admin'].includes(role);

  const cols = [
    { header: 'Emp ID', key: 'emp_code', render: (v) => v || '—' },
    { header: 'Employee', key: 'employee_name', render: (v) => v || '—' },
    { header: 'Working Days', key: 'days_in_month', render: (v) => v ?? '—' },
    { header: 'Days Worked', key: 'days_worked', render: (v) => v != null ? Number(v).toFixed(1) : '—' },
    { header: 'Basic', key: 'basic_salary', render: (v) => v ? `₹${Number(v).toLocaleString('en-IN')}` : '—' },
    { header: 'Gross', key: 'gross_salary', render: (v) => v ? `₹${Number(v).toLocaleString('en-IN')}` : '—' },
    { header: 'Deductions', key: 'total_deductions', render: (v) => v ? `₹${Number(v).toLocaleString('en-IN')}` : '—' },
    {
      header: 'Net Pay', key: 'net_salary',
      render: (v) => v ? <strong style={{ color: theme.success }}>₹{Number(v).toLocaleString('en-IN')}</strong> : '—'
    },
    { header: 'Status', key: 'status', render: (v) => <Badge status={v || 'pending'} /> },
  ];

  const inp = { padding: '0.5rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none', background: theme.white };

  const statusInfo = period ? STATUS_FLOW[period.status] : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>Payroll Management</h1>

        {/* Month / Year selector */}
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={selMonth} onChange={(e) => setSelMonth(Number(e.target.value))} style={inp}>
            {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
          </select>
          <select value={selYear} onChange={(e) => setSelYear(Number(e.target.value))} style={inp}>
            {[2023, 2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
          </select>

          {!period && canRun && (
            <Button variant="primary" onClick={() => setRunModal(true)}>
              ▶ Run Payroll
            </Button>
          )}
          {period?.status === 'draft' && canRun && (
            <Button variant="outlineGray" onClick={() => setRunModal('rerun')}>
              ↺ Re-run Payroll
            </Button>
          )}
          {period?.status === 'draft' && canHrApprove && (
            <Button variant="primary" onClick={handleHrApprove}>
              ✓ Approve &amp; Forward to CFO
            </Button>
          )}
          {period?.status === 'pending_cfo' && canCfoApprove && (
            <Button variant="primary" onClick={handleCfoApprove}>
              ✓ Final Approve
            </Button>
          )}
        </div>
      </div>

      {/* Status banner */}
      {period && statusInfo && (
        <div style={{ background: `${statusInfo.color}15`, border: `1.5px solid ${statusInfo.color}40`, borderRadius: '0.625rem', padding: '0.75rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: statusInfo.color, display: 'inline-block', flexShrink: 0 }} />
          <span style={{ fontWeight: 600, color: statusInfo.color, fontSize: '0.875rem' }}>
            {MONTHS[selMonth - 1]} {selYear} — {statusInfo.label}
          </span>
          {period.status === 'draft' && (
            <span style={{ fontSize: '0.8rem', color: theme.textMuted, marginLeft: '0.5rem' }}>
              Review records below and approve to forward to CFO.
            </span>
          )}
          {period.status === 'pending_cfo' && (
            <span style={{ fontSize: '0.8rem', color: theme.textMuted, marginLeft: '0.5rem' }}>
              HR has approved. Awaiting CFO final approval.
            </span>
          )}
          {(period.status === 'approved' || period.status === 'paid') && (
            <span style={{ fontSize: '0.8rem', color: theme.textMuted, marginLeft: '0.5rem' }}>
              Payslips are visible to employees.
            </span>
          )}
        </div>
      )}

      {!period && (
        <Card>
          <div style={{ textAlign: 'center', padding: '3rem 0' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>📅</div>
            <p style={{ fontWeight: 600, color: theme.text, marginBottom: '0.5rem' }}>
              No payroll run for {MONTHS[selMonth - 1]} {selYear}
            </p>
            {canRun
              ? <p style={{ color: theme.textMuted, fontSize: '0.875rem' }}>Click "Run Payroll" to generate payroll from Pay Fixation and attendance data.</p>
              : <p style={{ color: theme.textMuted, fontSize: '0.875rem' }}>Payroll has not been run for this month yet.</p>
            }
          </div>
        </Card>
      )}

      {period && (
        <Card padding="0">
          <Table
            columns={cols}
            data={records}
            loading={loading}
            emptyMessage="No payroll records for this period"
          />
        </Card>
      )}

      {/* Run / Re-run Payroll modal */}
      <Modal
        isOpen={!!runModal}
        onClose={() => setRunModal(false)}
        title={runModal === 'rerun' ? 'Re-run Payroll' : 'Run Payroll'}
        footer={
          <>
            <Button variant="outlineGray" onClick={() => setRunModal(false)}>Cancel</Button>
            <Button variant="primary" loading={running} onClick={() => handleRunPayroll(runModal === 'rerun')}>
              {runModal === 'rerun' ? 'Re-run Payroll' : 'Run Payroll'}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {runModal === 'rerun' ? (
            <>
              <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '0.5rem', padding: '0.875rem 1rem', fontSize: '0.875rem', color: '#856404' }}>
                <strong>Warning:</strong> This will delete all existing payroll records for <strong>{MONTHS[selMonth - 1]} {selYear}</strong> and recalculate from scratch using the latest Pay Fixation and attendance data.
              </div>
              <p style={{ color: theme.textMuted, fontSize: '0.8rem', margin: 0 }}>
                Use this to fix incorrect values. The period will remain in draft for HR review.
              </p>
            </>
          ) : (
            <>
              <p style={{ color: theme.text, margin: 0 }}>
                This will automatically generate payroll for <strong>{MONTHS[selMonth - 1]} {selYear}</strong> for all active employees based on their Pay Fixation and attendance records.
              </p>
              <div style={{ background: theme.lightBg, border: `1px solid ${theme.border}`, borderRadius: '0.5rem', padding: '0.875rem 1rem', fontSize: '0.85rem', color: theme.textMuted, lineHeight: 1.6 }}>
                <strong style={{ color: theme.text }}>How it works:</strong>
                <ul style={{ margin: '0.5rem 0 0 1.25rem', padding: 0 }}>
                  <li>Pulls the active Pay Fixation for each employee</li>
                  <li>Calculates attendance-based working days</li>
                  <li>Pro-rates salary components accordingly</li>
                  <li>Employees with no Pay Fixation are skipped</li>
                </ul>
              </div>
              <p style={{ color: theme.textMuted, fontSize: '0.8rem', margin: 0 }}>
                After running, HR Admin must review and approve before it goes to CFO.
              </p>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}

export default Payroll;
